import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Map, Marker, GeoJson, Overlay } from 'pigeon-maps';
import VehicleTrackingLocationSharePodTab from 'src/components/VehicleTrackingLocationShare/VehicleTrackingLocationSharePodTab';
import getColorCode from 'src/utils/GetThemeColor';
import {
  createVehicleLocationLogs,
  getVehicleLocationLogs,
  resetVehicleLocationLogs,
} from 'src/redux/Actions/Vehicle';
import { ReactComponent as PlayIcon } from 'src/Images/icons/common/play.svg';
import { ReactComponent as StopIcon } from 'src/Images/icons/common/stop.svg';
import TruckXs from 'src/Images/icons/truck/truck-xs.png';
import TruckSm from 'src/Images/icons/truck/truck-sm.png';
import TruckMd from 'src/Images/icons/truck/truck-md.png';
import TruckLg from 'src/Images/icons/truck/truck-lg.png';
import TruckXl from 'src/Images/icons/truck/truck-xl.png';
import 'src/style/detailPage.css';
import 'src/style/targetvsactualv2.css';
import 'src/style/vehicleDetail.css';
import 'src/style/vehicleTrackingLocationShare.css';

const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};
const TRACK_INTERVAL = 20 * 1000;
const BATCH_SIZE = 5;
const locationShareBottomPadding = 16;
const defaultMapCenter = [20.5937, 78.9629];

function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported on this device.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
          time: Date.now(),
        });
      },
      () => reject(new Error('Unable to capture your current location.')),
      GEOLOCATION_OPTIONS,
    );
  });
}

function normalizeLocations(locations = []) {
  return locations
    .map((location, index) => ({
      id: location.id || location.time || `location-${index}`,
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
    }))
    .filter(
      (location) =>
        Number.isFinite(location.latitude) &&
        Number.isFinite(location.longitude) &&
        location.latitude >= -90 &&
        location.latitude <= 90 &&
        location.longitude >= -180 &&
        location.longitude <= 180,
    );
}

function getMapCenter(locations) {
  if (!locations.length) return defaultMapCenter;

  const latitudes = locations.map((location) => location.latitude);
  const longitudes = locations.map((location) => location.longitude);

  return [
    (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
    (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
  ];
}

function getRouteZoom(locations) {
  if (locations.length <= 1) return 14;

  const latitudes = locations.map((location) => location.latitude);
  const longitudes = locations.map((location) => location.longitude);
  const latitudeSpan = Math.max(...latitudes) - Math.min(...latitudes);
  const longitudeSpan = Math.max(...longitudes) - Math.min(...longitudes);
  const maxSpan = Math.max(latitudeSpan, longitudeSpan);

  if (maxSpan > 20) return 3;
  if (maxSpan > 10) return 4;
  if (maxSpan > 5) return 5;
  if (maxSpan > 2) return 6;
  if (maxSpan > 1) return 8;
  if (maxSpan > 0.5) return 9;
  if (maxSpan > 0.2) return 10;
  if (maxSpan > 0.1) return 11;
  if (maxSpan > 0.05) return 12;
  return 13;
}

function RouteEndpoint({ color, label }) {
  return (
    <div className="vehicle-location-endpoint" title={label}>
      <svg width={22} height={22} viewBox="0 0 22 22" aria-hidden="true">
        <circle cx="11" cy="11" r="9" fill="#ffffff" />
        <circle cx="11" cy="11" r="6" fill={color} />
      </svg>
    </div>
  );
}

export default function VehicleTrackingLocationShare() {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber } = useParams();
  const [localLocations, setLocalLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('tracking');
  const [isLoading, setIsLoading] = useState(true);
  const [isTrackingActive, setIsTrackingActive] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [mapHeight, setMapHeight] = useState(360);
  const mapContainerRef = useRef(null);
  const trackingIntervalRef = useRef(null);
  const pendingBatchRef = useRef([]);
  const vehicleLocationLogs = useSelector(
    (state) => state.VehicleReducer.vehicleLocationLogs,
  );
  const backendLocations = vehicleLocationLogs.locations;
  const trackingDetails = vehicleLocationLogs.vehicleTracking;

  const themeColor = trackingDetails.color_theme;
  const themeHex = getColorCode(themeColor);
  const vehicleCapacity = Number(trackingDetails.capacity);
  const plottedLocations = useMemo(
    () => normalizeLocations([...backendLocations, ...localLocations]),
    [backendLocations, localLocations],
  );
  const mapCenter = useMemo(
    () => getMapCenter(plottedLocations),
    [plottedLocations],
  );
  const mapZoom = useMemo(
    () => getRouteZoom(plottedLocations),
    [plottedLocations],
  );
  const firstLocation = plottedLocations[0];
  const latestLocation = plottedLocations[plottedLocations.length - 1];
  const mapKey = latestLocation
    ? `${firstLocation?.id || 'start'}-${latestLocation.id || 'end'}-${plottedLocations.length}`
    : 'empty';

  const updateMapHeight = useCallback(() => {
    if (!mapContainerRef.current) return;

    const { top } = mapContainerRef.current.getBoundingClientRect();
    const availableHeight =
      window.innerHeight - top - locationShareBottomPadding;

    setMapHeight(Math.max(360, Math.floor(availableHeight)));
  }, []);

  const fetchLocationLogs = useCallback(() => {
    const endDateTime = new Date();
    const startDateTime = new Date(endDateTime);
    const startDate = new Date(
      startDateTime.setDate(startDateTime.getDate() - 2) -
        startDateTime.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split('T')[0];
    const endDate = new Date(
      endDateTime.getTime() - endDateTime.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split('T')[0];

    setIsLoading(true);
    return dispatch(
      getVehicleLocationLogs(null, null, {
        vehicleTrackingNumber,
        startDate,
        startTime: startDateTime.toTimeString().split(' ')[0],
        endDate,
        endTime: endDateTime.toTimeString().split(' ')[0],
      }),
    )
      .then(() => {
        setLocationError('');
      })
      .catch(() => {
        setLocationError('Unable to load vehicle tracking location.');
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, vehicleTrackingNumber]);

  useEffect(() => {
    fetchLocationLogs();

    return () => {
      dispatch(resetVehicleLocationLogs());
    };
  }, [dispatch, fetchLocationLogs]);

  useEffect(
    () => () => {
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    updateMapHeight();
    window.addEventListener('resize', updateMapHeight);

    return () => {
      window.removeEventListener('resize', updateMapHeight);
    };
  }, [activeTab, isLoading, plottedLocations.length, updateMapHeight]);

  async function sendLocationLogs(shouldRefresh = false) {
    const batch = pendingBatchRef.current;
    if (!batch.length) return true;

    pendingBatchRef.current = [];

    try {
      await dispatch(
        createVehicleLocationLogs({
          vehicle_tracking_number: vehicleTrackingNumber,
          location_logs: batch.map((location) => ({
            latitude: location.latitude,
            longitude: location.longitude,
          })),
        }),
      );
      if (shouldRefresh) {
        await fetchLocationLogs();
      }
      return true;
    } catch {
      pendingBatchRef.current = [...batch, ...pendingBatchRef.current];
      setLocationError('Unable to send vehicle location.');
      return false;
    }
  }

  async function captureVehicleLocation() {
    try {
      const location = await getCurrentLocation();
      setLocalLocations((prev) => [...prev, location]);
      pendingBatchRef.current = [...pendingBatchRef.current, location];
      setLocationError('');

      if (pendingBatchRef.current.length >= BATCH_SIZE) {
        await sendLocationLogs(false);
      }

      return location;
    } catch (error) {
      setLocationError(error.message || 'Unable to capture location.');
      return null;
    }
  }

  async function startTracking() {
    if (isTrackingActive) return;

    setIsTrackingActive(true);
    const location = await captureVehicleLocation();

    if (!location) {
      setIsTrackingActive(false);
      return;
    }

    trackingIntervalRef.current = setInterval(
      captureVehicleLocation,
      TRACK_INTERVAL,
    );
  }

  async function stopTracking() {
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }

    setIsTrackingActive(false);
    await captureVehicleLocation();
    const isSent = await sendLocationLogs(false);
    if (isSent) {
      await fetchLocationLogs();
      setLocalLocations([]);
    }
  }

  function renderTrackingMap() {
    if (isLoading) {
      return (
        <div className="vehicle-master-location-map">
          <span className="text-sm-outfit text-medium-bold">
            Loading vehicle locations...
          </span>
        </div>
      );
    }

    if (!plottedLocations.length) {
      return (
        <div className="vehicle-master-location-map">
          <div className="div-flex-column div-align-center">
            <span className="text-lg text-semi-bold">
              Location logs not added yet!
            </span>
            <span className="text-grey">
              Start by sharing this vehicle location.
            </span>
          </div>
          <button
            type="button"
            className={`vehicle-data-add-btn vehicle-tracking-location-share-empty-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center cg-10`}
            onClick={isTrackingActive ? stopTracking : startTracking}
          >
            {isTrackingActive ? (
              <StopIcon className="icon-size-md" />
            ) : (
              <PlayIcon className="icon-size-md" />
            )}
            <span className="text-md text-semi-bold">
              {isTrackingActive ? 'Stop' : 'Start'}
            </span>
          </button>
        </div>
      );
    }

    return (
      <div
        ref={mapContainerRef}
        className="vehicle-location-map-shell vehicle-tracking-location-share-map"
      >
        <Map
          key={mapKey}
          height={mapHeight}
          defaultCenter={mapCenter}
          defaultZoom={mapZoom}
        >
          {plottedLocations.length > 1 && (
            <GeoJson
              data={{
                type: 'FeatureCollection',
                features: [
                  {
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: plottedLocations.map((location) => [
                        location.longitude,
                        location.latitude,
                      ]),
                    },
                  },
                ],
              }}
              styleCallback={() => ({
                stroke: themeHex,
                strokeWidth: 4,
              })}
            />
          )}

          {plottedLocations.length > 1 && (
            <Overlay
              anchor={[latestLocation.latitude, latestLocation.longitude]}
              offset={[11, 11]}
            >
              <RouteEndpoint color="#e7332f" label="Latest location" />
            </Overlay>
          )}

          {plottedLocations.length > 1 && (
            <Overlay
              anchor={[firstLocation.latitude, firstLocation.longitude]}
              offset={[11, 11]}
            >
              <RouteEndpoint color="#667085" label="Start location" />
            </Overlay>
          )}

          {plottedLocations.length === 1 && (
            <Marker
              anchor={[firstLocation.latitude, firstLocation.longitude]}
              width={40}
            />
          )}
        </Map>
      </div>
    );
  }

  return (
    <div className="vehicle-tracking-location-share-page">
      <div
        className="vehicle-tracking-location-share-header"
        style={{ backgroundColor: themeHex }}
      >
        <span className="text-xl-outfit text-semi-bold">
          {trackingDetails.tenant_name}
        </span>
      </div>

      <div className="vehicle-tracking-location-share-content">
        <div className="vehicle-tracking-location-share-summary border-bottom">
          <div className="div-flex-row cg-10 div-align-center">
            {vehicleCapacity <= 100 ? (
              <img
                src={TruckXs}
                alt="truck"
                className="vehicle-tracking-location-share-truck"
              />
            ) : vehicleCapacity <= 300 ? (
              <img
                src={TruckSm}
                alt="truck"
                className="vehicle-tracking-location-share-truck"
              />
            ) : vehicleCapacity <= 500 ? (
              <img
                src={TruckMd}
                alt="truck"
                className="vehicle-tracking-location-share-truck"
              />
            ) : vehicleCapacity <= 800 ? (
              <img
                src={TruckLg}
                alt="truck"
                className="vehicle-tracking-location-share-truck"
              />
            ) : (
              <img
                src={TruckXl}
                alt="truck"
                className="vehicle-tracking-location-share-truck"
              />
            )}
            <span className="text-xl-outfit text-semi-bold">
              {trackingDetails.vehicle_number}
            </span>
          </div>

          <button
            type="button"
            className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center cg-5`}
            onClick={isTrackingActive ? stopTracking : startTracking}
          >
            {isTrackingActive ? (
              <StopIcon className="icon-size-sm" />
            ) : (
              <PlayIcon className="icon-size-sm" />
            )}
            <span className="text-sm-outfit text-medium-bold">
              {isTrackingActive ? 'Stop' : 'Start'}
            </span>
          </button>
        </div>

        <div className="div-flex-column">
          <div className="div-flex-row position-relative border-bottom tab-view-bg w-100">
            <div className="tab-scroll-wrapper div-flex-row no-scrollbar no-side-padding">
              <div
                onClick={() => setActiveTab('tracking')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setActiveTab('tracking');
                }}
                className={`div-flex-row div-align-center text-sm-outfit text-medium-bold cg-5 tab-selection ${
                  activeTab === 'tracking'
                    ? `theme-tab-active-${themeColor} theme-text-color-${themeColor}`
                    : 'secondary-text-grey'
                }`}
                role="button"
                tabIndex={0}
              >
                <span className="text-nowrap">Tracking</span>
              </div>
              <div
                onClick={() => setActiveTab('pod')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setActiveTab('pod');
                }}
                className={`div-flex-row div-align-center text-sm-outfit text-medium-bold cg-5 tab-selection ${
                  activeTab === 'pod'
                    ? `theme-tab-active-${themeColor} theme-text-color-${themeColor}`
                    : 'secondary-text-grey'
                }`}
                role="button"
                tabIndex={0}
              >
                <span className="text-nowrap">POD</span>
              </div>
            </div>
          </div>

          <div className="vehicle-tracking-location-share-tab-content">
            {activeTab === 'tracking' && renderTrackingMap()}
            {activeTab === 'pod' && (
              <VehicleTrackingLocationSharePodTab
                vehicleTrackingNumber={vehicleTrackingNumber}
                themeColor={themeColor}
              />
            )}
            {locationError && (
              <span className="text-sm-outfit vehicle-location-error">
                {locationError}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
